<app-stepper-form
  [header]="header"
  [subheader]="subheader"
  [formIcon]="formIcon"
  [showSteps]="true"
  [steps]="steps"
  [currentStep]="currentStep"
  [currentStepIndex]="currentStepIndex"
  [isLoading]="isLoading"
  [form]="stepperForm"
>
  <div class="step-content mh-200" [formGroup]="stepperForm">
    <app-stepper-form-description
      [formKey]="formKey"
      [hidden]="currentStep !== StepsEnum.FormDescription"
    />


    <!--  ================================================================================================================  -->


    <section
      class="step-section"
      [hidden]="currentStep !== StepsEnum.TrustDetailsAndAddress"
      [formGroupName]="StepsEnum.TrustDetailsAndAddress"
    >
      <h1 class="h1-heading">{{ steps[currentStepIndex].label }}</h1>

      <div class="form-group">
        <app-integrations-entity-search-control
          label="Trust Name"
          formControlName="trustName"
          [entityType]="EntityType.Trust"
          (trustSelected)="onTrustSelected($event)"
        />
      </div>

      <div class="form-group">
        <app-flags-autocomplete
          label="Jurisdiction"
          formControlName="jurisdiction"
          [options]="jurisdictionOptions"
        />
      </div>

      <div class="form-group">
        <app-input-number
          label="Settled Sum"
          formControlName="settledSum"
          [min]="0"
          [tooltipMessage]="settledSumTooltipMessage"
        />
      </div>

      <div class="form-group">
        <app-address-form-group
          label="Meeting Address"
          [formGroup]="trustDetailsForm.controls.meetingAddress"
        />
      </div>
    </section>


    <!--  ================================================================================================================  -->


    <section
      class="step-section"
      [hidden]="currentStep !== StepsEnum.SettlorAndPrincipalAppointor"
      [formGroupName]="StepsEnum.SettlorAndPrincipalAppointor"
    >
      <h1 class="h1-heading">{{ steps[currentStepIndex].label }}</h1>

      <div class="form-group" [formGroup]="settlorAndAppointorForm.controls.settlor">
        <h2 class="h2-heading">Settlor</h2>

        <div class="form-group d-flex gap-2">
          <app-select
            class="flex-grow-1"
            label="Settlor Type"
            formControlName="trusteeType"
            [valuePrimitive]="true"
            [options]="individualCorporateTrusteeOptions"
          />
        </div>

        @if (settlorAndAppointorForm.controls.settlor.controls.trusteeType.value == IndividualCorporateRelationship.Individual) {
          <div class="form-group">
            <app-relationship-form-group
              nameControlLabel="Name"
              [formGroup]="settlorAndAppointorForm.controls.settlor.controls.individualTrustee"
            />
          </div>
        } @else if (settlorAndAppointorForm.controls.settlor.controls.trusteeType.value == IndividualCorporateRelationship.Corporate) {
          <div class="form-group">
            <app-corporate-relationship-form-group
              [hideIsOverseasCompany]="true"
              [formGroup]="settlorAndAppointorForm.controls.settlor.controls.corporateTrustee"
            />
          </div>
        }

      </div>

      <app-divider margin="24px 0"/>

      <ul class="list-unstyled">

        @for (appointorsFormGroup of appointorsFormArray.controls; track appointorsFormGroup) {
          <li class="form-group" [formGroup]="appointorsFormGroup">

            <div class="d-flex align-items-center justify-content-between">
              <h3 class="h3-heading mb-2">Principal (Appointor) <span
                [hidden]="appointorsFormArray.controls.length <= 1">{{ $index + 1 }}</span></h3>

              @if (appointorsFormArray.controls.length > 1) {
                <button
                  type="button"
                  class="btn btn-secondary btn-icon"
                  (click)="removeAppointor($index)"
                >
                  <i class="icon ic-close-20"></i>
                </button>
              }
            </div>

            <div class="form-group">
              <app-relationship-form-group
                nameControlLabel="Name of Principal"
                [formGroup]="appointorsFormGroup"
              />
            </div>
          </li>

          @if (appointorsFormArray.controls.length > 1) {
            <li>
              <app-divider margin="24px 0"/>
            </li>
          }
        }
      </ul>

      <div>
        <button
          type="button"
          class="btn btn-primary d-flex align-items-center gap-8"
          [disabled]="appointorsFormArray.controls.length >= maxPrincipalAppointorsToAdd"
          (click)="addAppointor()"
        >
          <i class="icon ic-add-20"></i>
          <span>Add Appointor</span>
        </button>
      </div>
    </section>


    <!--  ================================================================================================================  -->


    <section
      class="step-section"
      [hidden]="currentStep !== StepsEnum.Trustees"
      [formGroupName]="StepsEnum.Trustees"
    >
      <h1 class="h1-heading">{{ steps[currentStepIndex].label }}</h1>


      <ul class="trustees list-unstyled m-0">
        @for (trusteeFrom of trusteesFormArray.controls; track trusteeFrom) {
          <li
            class="trustee-item form-group"
            [class]="{ 'border-0': isAddTrusteeButtonHidden, 'mb-0': isAddTrusteeButtonHidden }"
            [formGroup]="trusteeFrom"
          >
            @if (trusteesFormArray.length > 1) {
              <div class="d-flex align-items-center justify-content-between">
                <h3 class="h3-heading mb-2">Trustee {{ $index + 1 }}</h3>

                <button
                  type="button"
                  class="btn btn-secondary btn-icon"
                  (click)="removeTrustee($index)"
                >
                  <i class="icon ic-close-20"></i>
                </button>
              </div>
            }

            <div class="form-group d-flex gap-2">
              <app-select
                class="flex-grow-1"
                label="Trustee Type"
                formControlName="trusteeType"
                [valuePrimitive]="true"
                [options]="individualCorporateTrusteeOptions"
              />
            </div>

            @if (trusteeFrom.controls.trusteeType.value == IndividualCorporateRelationship.Individual) {
              <div class="form-group">
                <app-relationship-form-group
                  nameControlLabel="Name of Trustee"
                  [formGroup]="trusteeFrom.controls.individualTrustee"
                />
              </div>

              <div class="form-group">
                <app-checkbox-group
                  formControlName="types"
                  label="Types"
                  [options]="relationshipTypesOptions"
                />
              </div>
            } @else if (trusteeFrom.controls.trusteeType.value == IndividualCorporateRelationship.Corporate) {
              <div class="form-group">
                <app-corporate-relationship-form-group
                  [hideIsOverseasCompany]="true"
                  [formGroup]="trusteeFrom.controls.corporateTrustee"
                />
              </div>
            }

          </li>
        }
      </ul>

      <div [hidden]="isAddTrusteeButtonHidden">
        <button
          type="button"
          class="btn btn-primary d-flex align-items-center gap-8"
          [disabled]="isAddTrusteeButtonDisabled"
          (click)="addTrustee()"
        >
          <i class="icon ic-add-20"></i>
          <span>Add Trustee</span>
        </button>
      </div>
    </section>


    <!--  ================================================================================================================  -->


    <section
      class="step-section"
      [hidden]="currentStep !== StepsEnum.Beneficiaries"
      [formGroupName]="StepsEnum.Beneficiaries"
    >
      <h1 class="h1-heading">{{ steps[currentStepIndex].label }}</h1>

      @if (isSimpleBeneficiariesFillingMode) {
        <div class="form-group">
          <app-button
            text="I would like to edit the Beneficiaries"
            (clickBtn)="setIsSimpleBeneficiariesFillingMode(false)"
          />
        </div>
      }

      <ul class="list-unstyled">
        @for (beneficiaryFormGroup of beneficiariesFormArray.controls; track beneficiaryFormGroup) {
          <li [formGroup]="beneficiaryFormGroup">
            @if (beneficiariesFormArray.controls.length > 1) {
              <div class="d-flex align-items-center justify-content-between">
                <h3 class="h3-heading mb-2">Beneficiary {{ $index + 1 }}</h3>

                <button
                  type="button"
                  class="btn btn-secondary btn-icon"
                  (click)="removeBeneficiary($index)"
                >
                  <i class="icon ic-close-20"></i>
                </button>
              </div>
            }

            <div class="form-group">
              <app-input
                label="Name"
                formControlName="name"
              />
            </div>

            <div class="form-group">
              <app-address-form-group
                [formGroup]="beneficiaryFormGroup.controls.address"
              />
            </div>
          </li>
        }
      </ul>

      <div>
        <button
          type="button"
          class="btn btn-primary d-flex align-items-center gap-8"
          (click)="addBeneficiary()"
        >
          <i class="icon ic-add-20"></i>
          <span>Add Beneficiary</span>
        </button>
      </div>
    </section>


    <!--  ================================================================================================================  -->


    <section
      class="step-section"
      [hidden]="currentStep !== StepsEnum.AuthorisedSignatories"
      [formGroupName]="StepsEnum.AuthorisedSignatories"
    >
      <app-authorised-signatories-step
        title="Authorised Signatories"
        [formGroup]="authorisedSignatoriesForm"
        [securityRegistryRecords]="authorisedSecurityRegistryRecords"
        [authSignatoriesValidators]="authSignatoriesValidators"
        (corporateHolderChange)="onCorporateHolderChangeChange($event)"
      />
    </section>
  </div>
</app-stepper-form>
